import { BRUSH_KINDS, DRAWING_LIMITS, type DrawingDTO, type StrokeDTO } from '@shared/protocol/messages';
import { Rng } from '../game/combat/rng';
import { t } from '../i18n';

export const GRID = DRAWING_LIMITS.size;

export function emptyDrawing(): DrawingDTO {
  return { w: GRID, h: GRID, strokes: [] };
}

export function pointCount(d: DrawingDTO): number {
  let n = 0;
  for (const s of d.strokes) n += s.p.length / 2;
  return n;
}

export function isBlank(d: DrawingDTO | null | undefined): boolean {
  return !d || d.strokes.every((s) => s.e);
}

/** Client-side mirror of the server's drawing validation. */
export function validateDrawing(d: DrawingDTO): string | null {
  if (d.w !== GRID || d.h !== GRID) return t('Drawing has the wrong size');
  if (d.strokes.length > DRAWING_LIMITS.maxStrokes) return t('Too many strokes (max {max})', { max: DRAWING_LIMITS.maxStrokes });
  if (pointCount(d) > DRAWING_LIMITS.maxPoints) return t('Drawing is too detailed — try fewer scribbles');
  for (const s of d.strokes) {
    if (!/^#[0-9a-fA-F]{6}$/.test(s.c)) return t('Bad colour');
    if (!Number.isInteger(s.s) || s.s < DRAWING_LIMITS.minBrush || s.s > DRAWING_LIMITS.maxBrush) return t('Bad brush size');
    if (s.p.length < 2 || s.p.length % 2 !== 0) return t('Bad stroke');
    if (s.b !== undefined && !BRUSH_KINDS.includes(s.b)) return t('Bad stroke');
    for (const v of s.p) if (!Number.isInteger(v) || v < 0 || v > GRID) return t('Stroke outside the page');
  }
  return null;
}

/** Ramer–Douglas–Peucker simplification on a flat [x,y,...] list. */
export function simplify(points: number[], epsilon: number): number[] {
  const n = points.length / 2;
  if (n <= 2) return points.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const ax = points[a * 2]!;
    const ay = points[a * 2 + 1]!;
    const bx = points[b * 2]!;
    const by = points[b * 2 + 1]!;
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    let maxD = 0;
    let idx = -1;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs(dy * points[i * 2]! - dx * points[i * 2 + 1]! + bx * ay - by * ax) / len;
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (maxD > epsilon && idx > 0) {
      keep[idx] = 1;
      stack.push([a, idx], [idx, b]);
    }
  }
  const out: number[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(points[i * 2]!, points[i * 2 + 1]!);
  return out;
}

export interface RenderOptions {
  /** Frame seed for line boil; 0 = exact strokes. */
  boilSeed?: number;
  boilAmount?: number;
}

function strokePath(ctx: CanvasRenderingContext2D, pts: number[], scale: number, jitter: (i: number) => [number, number]): void {
  ctx.beginPath();
  const n = pts.length / 2;
  const [jx0, jy0] = jitter(0);
  const x0 = (pts[0]! + jx0) * scale;
  const y0 = (pts[1]! + jy0) * scale;
  ctx.moveTo(x0, y0);
  if (n === 1) {
    ctx.lineTo(x0 + 0.01, y0);
    return;
  }
  for (let i = 1; i < n - 1; i++) {
    const [jx, jy] = jitter(i);
    const [nx, ny] = jitter(i + 1);
    const x = (pts[i * 2]! + jx) * scale;
    const y = (pts[i * 2 + 1]! + jy) * scale;
    const mx = ((pts[i * 2]! + jx + pts[i * 2 + 2]! + nx) / 2) * scale;
    const my = ((pts[i * 2 + 1]! + jy + pts[i * 2 + 3]! + ny) / 2) * scale;
    ctx.quadraticCurveTo(x, y, mx, my);
  }
  const [jl, jm] = jitter(n - 1);
  ctx.lineTo((pts[(n - 1) * 2]! + jl) * scale, (pts[(n - 1) * 2 + 1]! + jm) * scale);
}

/** Pixel strokes: one s×s square per point (its top-left corner); erasers clear squares. */
function drawPixels(ctx: CanvasRenderingContext2D, s: StrokeDTO, scale: number): void {
  if (s.e) ctx.globalCompositeOperation = 'destination-out';
  ctx.globalAlpha = 1;
  ctx.fillStyle = s.e ? '#000000' : s.c;
  // Whole device pixels, a hair wider, so neighbouring squares never show a seam when scaled.
  const w = Math.ceil(s.s * scale) + 0.5;
  for (let i = 0; i < s.p.length; i += 2) ctx.fillRect(Math.floor(s.p[i]! * scale), Math.floor(s.p[i + 1]! * scale), w, w);
  ctx.globalCompositeOperation = 'source-over';
}

/** Small deterministic generator seeded from the stroke itself, so spray dots stay put between redraws. */
function strokeSeed(s: StrokeDTO): () => number {
  let h = 2166136261 ^ s.s;
  for (let i = 0; i < Math.min(s.p.length, 16); i++) h = Math.imul(h ^ s.p[i]!, 16777619);
  let x = h >>> 0 || 1;
  return () => {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    return (x >>> 0) / 4294967296;
  };
}

/** Calls `at` every `step` grid units along the stroke. */
function walk(p: number[], step: number, at: (x: number, y: number) => void): void {
  at(p[0]!, p[1]!);
  for (let i = 2; i < p.length; i += 2) {
    const x0 = p[i - 2]!;
    const y0 = p[i - 1]!;
    const n = Math.max(1, Math.round(Math.hypot(p[i]! - x0, p[i + 1]! - y0) / step));
    for (let k = 1; k <= n; k++) at(x0 + ((p[i]! - x0) * k) / n, y0 + ((p[i + 1]! - y0) * k) / n);
  }
}

function drawSpray(ctx: CanvasRenderingContext2D, s: StrokeDTO, scale: number): void {
  const rnd = strokeSeed(s);
  const r = s.s / 2;
  const dots = Math.max(6, Math.round(s.s * 1.2));
  const dot = Math.max(0.5, s.s / 14) * scale;
  ctx.fillStyle = s.c;
  ctx.globalAlpha = 0.85;
  walk(s.p, Math.max(1, s.s / 3), (x, y) => {
    for (let i = 0; i < dots; i++) {
      // Denser in the middle, like a real spray can.
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(rnd()) * r * (0.35 + rnd() * 0.65);
      ctx.fillRect((x + Math.cos(a) * d) * scale, (y + Math.sin(a) * d) * scale, dot, dot);
    }
  });
  ctx.globalAlpha = 1;
}

/** A broad-nib pen held at 45°: thick going one way, a hairline the other. */
function drawCalligraphy(ctx: CanvasRenderingContext2D, s: StrokeDTO, scale: number, jitter: (i: number) => [number, number]): void {
  const nx = Math.SQRT1_2 * (s.s / 2);
  const ny = -Math.SQRT1_2 * (s.s / 2);
  ctx.fillStyle = s.c;
  ctx.strokeStyle = s.c;
  // Opaque, so the joins between nib segments never show as seams.
  ctx.globalAlpha = 1;
  ctx.lineWidth = Math.max(1, s.s * 0.12 * scale);
  const n = s.p.length / 2;
  for (let i = 0; i < Math.max(1, n - 1); i++) {
    const k = Math.min(n - 1, i + 1);
    const [ja, jb] = jitter(i);
    const [jc, jd] = jitter(k);
    const x0 = s.p[i * 2]! + ja;
    const y0 = s.p[i * 2 + 1]! + jb;
    const x1 = s.p[k * 2]! + jc;
    const y1 = s.p[k * 2 + 1]! + jd;
    ctx.beginPath();
    ctx.moveTo((x0 + nx) * scale, (y0 + ny) * scale);
    ctx.lineTo((x1 + nx) * scale, (y1 + ny) * scale);
    ctx.lineTo((x1 - nx) * scale, (y1 - ny) * scale);
    ctx.lineTo((x0 - nx) * scale, (y0 - ny) * scale);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

/** Waxy grain for the crayon: a noise tile in the stroke colour, made once per colour. */
const grainCache = new Map<string, CanvasPattern | null>();
function crayonGrain(ctx: CanvasRenderingContext2D, colour: string): CanvasPattern | null {
  const hit = grainCache.get(colour);
  if (hit !== undefined) return hit;
  if (grainCache.size > 64) grainCache.clear();
  let pattern: CanvasPattern | null = null;
  const tile = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  const tc = tile?.getContext('2d');
  if (tile && tc) {
    tile.width = 12;
    tile.height = 12;
    const img = tc.createImageData(12, 12);
    const rgb = [1, 3, 5].map((i) => parseInt(colour.slice(i, i + 2), 16));
    let x = 0x9e3779b9;
    for (let i = 0; i < 12 * 12; i++) {
      x ^= x << 13;
      x ^= x >>> 17;
      x ^= x << 5;
      const v = (x >>> 0) / 4294967296;
      img.data.set([rgb[0]!, rgb[1]!, rgb[2]!, v < 0.34 ? 0 : v < 0.5 ? 120 : 245], i * 4);
    }
    tc.putImageData(img, 0, 0);
    pattern = ctx.createPattern(tile, 'repeat');
  }
  grainCache.set(colour, pattern);
  return pattern;
}

/** Marker, crayon and watercolour: path brushes with their own ink. */
function drawInk(ctx: CanvasRenderingContext2D, s: StrokeDTO, scale: number, jitter: (i: number) => [number, number]): void {
  const pass = (alpha: number, width: number, style: string | CanvasPattern = s.c): void => {
    ctx.strokeStyle = style;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = width * scale;
    strokePath(ctx, s.p, scale, jitter);
    ctx.stroke();
  };
  if (s.b === 'marker') pass(1, s.s);
  else if (s.b === 'watercolor') {
    // See-through washes: overlapping strokes get darker.
    pass(0.22, s.s);
    pass(0.16, s.s * 0.6);
  } else {
    // Crayon: a light wax base, then the grain with paper showing through.
    pass(0.28, s.s * 0.8);
    const grain = crayonGrain(ctx, s.c);
    if (grain) pass(0.95, s.s, grain);
  }
  ctx.globalAlpha = 1;
}

/** Draw one stroke in its brush's look; the default coloured pencil is a solid core + grainy lighter pass. */
export function drawStroke(ctx: CanvasRenderingContext2D, s: StrokeDTO, scale: number, rng: Rng | null, amount: number): void {
  if (s.b === 'pixel') return drawPixels(ctx, s, scale);
  const jit = (): [number, number] => (rng ? [(rng.float() - 0.5) * amount, (rng.float() - 0.5) * amount] : [0, 0]);
  const offsets: [number, number][] = [];
  for (let i = 0; i < s.p.length / 2; i++) offsets.push(jit());
  const jitter = (i: number): [number, number] => offsets[i] ?? [0, 0];
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (!s.e && s.b === 'spray') return drawSpray(ctx, s, scale);
  if (!s.e && s.b === 'calligraphy') return drawCalligraphy(ctx, s, scale, jitter);
  if (!s.e && (s.b === 'marker' || s.b === 'watercolor' || s.b === 'crayon')) return drawInk(ctx, s, scale, jitter);
  if (s.e) {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.globalAlpha = 1;
    ctx.lineWidth = s.s * scale;
    strokePath(ctx, s.p, scale, jitter);
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
    return;
  }
  ctx.strokeStyle = s.c;
  ctx.globalAlpha = 0.92;
  ctx.lineWidth = s.s * scale;
  strokePath(ctx, s.p, scale, jitter);
  ctx.stroke();
  // Second, thinner and lighter pass slightly offset: graphite grain.
  ctx.globalAlpha = 0.35;
  ctx.lineWidth = Math.max(1, s.s * scale * 0.45);
  ctx.save();
  ctx.translate(0.6 * scale, 0.5 * scale);
  strokePath(ctx, s.p, scale, jitter);
  ctx.stroke();
  ctx.restore();
  ctx.globalAlpha = 1;
}

export function renderDrawing(ctx: CanvasRenderingContext2D, d: DrawingDTO, size: number, opts: RenderOptions = {}): void {
  const scale = size / d.w;
  const rng = opts.boilSeed ? new Rng(opts.boilSeed) : null;
  for (const s of d.strokes) drawStroke(ctx, s, scale, rng, opts.boilAmount ?? 1.6);
}

/** Bounding box of visible ink in grid units, for centring custom art. */
export function inkBounds(d: DrawingDTO): { x: number; y: number; w: number; h: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const s of d.strokes) {
    if (s.e) continue;
    // Pixel squares hang down-right from their point; path brushes spread both ways.
    const [lo, hi] = s.b === 'pixel' ? [0, s.s] : [s.s / 2, s.s / 2];
    for (let i = 0; i < s.p.length; i += 2) {
      minX = Math.min(minX, s.p[i]! - lo);
      maxX = Math.max(maxX, s.p[i]! + hi);
      minY = Math.min(minY, s.p[i + 1]! - lo);
      maxY = Math.max(maxY, s.p[i + 1]! + hi);
    }
  }
  if (!Number.isFinite(minX)) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
